import React, { useState, useEffect, useRef } from 'react';
import { useCalendarSync } from '../hooks/useCalendarSync';

const API = 'https://calendar.nareea.co.uk';

const ChatAgent = ({ onCalendarUpdate }) => {
  const [mode, setMode] = useState('chat');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: "Hi! I'm your AI Calendar Assistant. You can ask me to modify Luke's calendar in natural language, or switch to Form mode for structured input.\n\nTry:\n• \"Move Board Growth Briefing to Wednesday May 27\"\n• \"Cancel Team Standup on May 27\"\n• \"Add CEO Call on May 29 at 15:00 for 30 minutes\"\n• \"Show all conflicts this week\"",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const { broadcast } = useCalendarSync();
  const [events, setEvents] = useState([]);
  const messagesEnd = useRef(null);
  const [pendingCancel, setPendingCancel] = useState(null);

  const [form, setForm] = useState({
    action: 'add',
    title: '',
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    type: 'meeting',
    priority: '5',
  });
  const [formMsg, setFormMsg] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/draft/latest`)
      .then(r => r.json())
      .then(data => setEvents(data.events || []))
      .catch(console.error);
  }, []);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const saveToArchive = async (userMsg, assistantMsg, changes) => {
    try {
      await fetch(`${API}/api/archive-dialog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          mode: 'chat',
          user_message: userMsg,
          assistant_response: assistantMsg,
          changes: changes || [],
        }),
      });
    } catch (e) {
      console.log('Archive save failed:', e.message);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);

    // Обработка подтверждения удаления
    if (pendingCancel) {
      if (text.toLowerCase() === 'yes' || text.toLowerCase() === 'да') {
        setLoading(true);
        try {
          const res = await fetch(`${API}/api/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message: `Cancel event with uid ${pendingCancel.uid}`,
              events,
              forceCancel: pendingCancel.uid,
            }),
          });
          const data = await res.json();
          if (data.updatedEvents) {
            setEvents(data.updatedEvents);
            if (onCalendarUpdate) onCalendarUpdate(data.updatedEvents);
          }
          broadcast('CALENDAR_UPDATED', { action: 'delete' });
          setMessages(prev => [...prev, { role: 'assistant', text: `Deleted: **${pendingCancel.title}**` }]);
        } catch (err) {
          setMessages(prev => [...prev, { role: 'assistant', text: 'Error deleting event.' }]);
        }
        setPendingCancel(null);
        setLoading(false);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', text: 'Deletion cancelled.' }]);
        setPendingCancel(null);
      }
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, events }),
      });
      const data = await res.json();

      // Перехватываем cancel — просим подтверждение
      if (data.understood && data.action === 'cancel' && data.changes?.length > 0) {
        const uid = data.changes[0].uid;
        const found = events.find(ev => ev.uid === uid) || { title: uid, date: '' };
        setPendingCancel({ uid, title: found.title, date: found.date });
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: `Found: **${found.title}**${found.date ? ' on ' + found.date : ''}.\n\nConfirm deletion? Type **Yes** to confirm or **No** to cancel.`,
        }]);
        setLoading(false);
        return;
      }

      if (data.error) {
        const errMsg = `Error: ${data.error}`;
        setMessages(prev => [...prev, { role: 'assistant', text: errMsg, error: true }]);
        await saveToArchive(text, errMsg, []);
      } else {
        const hasChanges = data.changes && data.changes.length > 0;
        let responseText = data.message || data.explanation || 'Done.';

        if (hasChanges) {
          responseText += '\n\n**Changes made:**\n';
          data.changes.forEach(c => {
            responseText += `• ${c.field}: ${c.old_value} → ${c.new_value}\n`;
          });
          if (data.updatedEvents) {
            setEvents(data.updatedEvents);
            if (onCalendarUpdate) onCalendarUpdate(data.updatedEvents);
          }
          broadcast('CALENDAR_UPDATED', { changes: data.changes });
        }

        setMessages(prev => [...prev, {
          role: 'assistant',
          text: responseText,
          changes: data.changes,
          understood: data.understood,
        }]);

        await saveToArchive(text, responseText, data.changes || []);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: `Connection error: ${err.message}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async () => {
    if (!form.title || !form.date || !form.startTime) {
      setFormMsg('Please fill in Title, Date and Start Time');
      return;
    }

    setFormLoading(true);
    setFormMsg('');

    try {
      let command = '';
      if (form.action === 'add') {
        command = `Add event "${form.title}" on ${form.date} from ${form.startTime} to ${form.endTime || form.startTime} at ${form.location || 'TBD'}, type: ${form.type}, priority: ${form.priority}`;
      } else if (form.action === 'move') {
        command = `Move event "${form.title}" to ${form.date} at ${form.startTime}`;
      } else if (form.action === 'cancel') {
        command = `Cancel event "${form.title}" on ${form.date}`;
      }

      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: command, events }),
      });
      const data = await res.json();

      if (data.error) {
        setFormMsg(data.error);
      } else {
        const hasChanges = data.changes && data.changes.length > 0;
        if (data.updatedEvents) {
          setEvents(data.updatedEvents);
          if (onCalendarUpdate) onCalendarUpdate(data.updatedEvents);
        }
        setFormMsg(hasChanges
          ? `${data.message || 'Done!'} (${data.changes.length} change(s) applied)`
          : `${data.message || data.explanation}`
        );
        await saveToArchive(command, data.message || data.explanation, data.changes || []);
        if (hasChanges) {
          setForm({ action: 'add', title: '', date: '', startTime: '', endTime: '', location: '', type: 'meeting', priority: '5' });
        }
      }
    } catch (err) {
      setFormMsg(`Error: ${err.message}`);
    } finally {
      setFormLoading(false);
    }
  };

  const formatText = (text) => {
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>');
  };

  const suggestions = [
    'Move Board Growth Briefing to 2026-05-27',
    'Show all events on May 26',
    'Reschedule CEO Urgent Call to 16:00',
    'What conflicts exist this week?',
  ];

  const inputStyle = {
    width: '100%', padding: '8px 12px', border: '1px solid #dadce0',
    borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  const labelStyle = {
    fontSize: 12, color: '#5f6368', fontWeight: 600, marginBottom: 4, display: 'block',
  };

  return (
    <div style={{ maxWidth: 700, margin: '20px auto', fontFamily: 'Google Sans, Arial, sans-serif', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)' }}>

      {/* Header */}
      <div style={{ background: '#fff', borderRadius: '12px 12px 0 0', padding: '14px 20px', borderBottom: '1px solid #dadce0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#1a73e8' }}>AI Calendar Chat</div>
            <div style={{ fontSize: 11, color: '#34a853' }}>{events.length} events loaded{pendingCancel ? ' · waiting for confirmation' : ''}</div>
          </div>
        </div>
        <div style={{ display: 'flex', background: '#f1f3f4', borderRadius: 20, padding: 3, gap: 2 }}>
          <button onClick={() => setMode('chat')} style={{ background: mode === 'chat' ? '#1a73e8' : 'none', color: mode === 'chat' ? '#fff' : '#5f6368', border: 'none', borderRadius: 16, padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Chat</button>
          <button onClick={() => setMode('form')} style={{ background: mode === 'form' ? '#1a73e8' : 'none', color: mode === 'form' ? '#fff' : '#5f6368', border: 'none', borderRadius: 16, padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Form</button>
        </div>
      </div>

      {mode === 'chat' && (
        <>
          <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f8f9fa', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '80%', padding: '12px 16px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.role === 'user' ? '#1a73e8' : '#fff',
                  color: msg.role === 'user' ? '#fff' : '#3c4043',
                  fontSize: 14, lineHeight: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: msg.role === 'assistant' ? '1px solid #dadce0' : 'none',
                }}>
                  {msg.role === 'assistant' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 11, color: '#5f6368' }}>
                      AI Assistant
                      {msg.understood === false && <span style={{ color: '#ea4335' }}>· needs clarification</span>}
                      {msg.changes?.length > 0 && <span style={{ color: '#34a853' }}>· {msg.changes.length} change(s) applied</span>}
                    </div>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: formatText(msg.text) }} />
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ padding: '12px 16px', background: '#fff', borderRadius: '16px 16px 16px 4px', border: '1px solid #dadce0', fontSize: 14, color: '#5f6368' }}>
                  Analyzing your request...
                </div>
              </div>
            )}
            <div ref={messagesEnd} />
          </div>

          {/* Quick confirm buttons when pending cancel */}
          {pendingCancel && (
            <div style={{ background: '#fce8e6', padding: '8px 16px', display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#c5221f', flex: 1 }}>Delete {pendingCancel.title}?</span>
              <button onClick={() => { setInput('Yes'); setTimeout(sendMessage, 100); }} style={{ background: '#ea4335', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Yes, Delete</button>
              <button onClick={() => { setPendingCancel(null); setMessages(prev => [...prev, { role: 'assistant', text: 'Deletion cancelled.' }]); }} style={{ background: '#f1f3f4', color: '#3c4043', border: 'none', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 13 }}>No, Keep</button>
            </div>
          )}

          <div style={{ background: '#fff', padding: '8px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => setInput(s)} style={{ background: '#e8f0fe', border: 'none', borderRadius: 12, padding: '4px 12px', fontSize: 11, color: '#1a73e8', cursor: 'pointer' }}>{s}</button>
            ))}
          </div>

          <div style={{ background: '#fff', borderRadius: '0 0 12px 12px', padding: '12px 16px', borderTop: '1px solid #dadce0', display: 'flex', gap: 10 }}>
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder={pendingCancel ? 'Type Yes or No...' : "Type a command..."}
              disabled={loading}
              style={{ flex: 1, padding: '10px 16px', border: `1px solid ${pendingCancel ? '#ea4335' : '#dadce0'}`, borderRadius: 20, fontSize: 14, outline: 'none' }}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              style={{ background: loading || !input.trim() ? '#dadce0' : '#1a73e8', color: '#fff', border: 'none', borderRadius: 20, padding: '10px 20px', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}
            >
              Send
            </button>
          </div>
        </>
      )}

      {mode === 'form' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, background: '#f8f9fa' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Action</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['add', 'move', 'cancel'].map(a => (
                  <button key={a} onClick={() => setForm(f => ({ ...f, action: a }))}
                    style={{ flex: 1, padding: '10px', border: `2px solid ${form.action === a ? '#1a73e8' : '#dadce0'}`, borderRadius: 8, background: form.action === a ? '#e8f0fe' : '#fff', color: form.action === a ? '#1a73e8' : '#5f6368', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {a === 'add' ? 'Add Event' : a === 'move' ? 'Move Event' : 'Cancel Event'}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Event Title *</label>
              {form.action === 'move' || form.action === 'cancel' ? (
                <select value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle}>
                  <option value="">— Select event —</option>
                  {events.map((ev, i) => (
                    <option key={i} value={ev.title}>{ev.title} ({ev.date} {ev.startTime})</option>
                  ))}
                </select>
              ) : (
                <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Board Strategy Meeting" style={inputStyle} />
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Date *</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Start Time *</label>
                <input type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>End Time</label>
                <input type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} style={inputStyle} />
              </div>
            </div>

            {form.action === 'add' && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Location</label>
                  <input type="text" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Conference Room A, Zoom" style={inputStyle} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  <div>
                    <label style={labelStyle}>Event Type</label>
                    <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={inputStyle}>
                      <option value="meeting">Meeting</option>
                      <option value="call">Call</option>
                      <option value="strategic">Strategic</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Priority (1-10): {form.priority}</label>
                    <input type="range" min="1" max="10" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={{ width: '100%', marginTop: 8 }} />
                  </div>
                </div>
              </>
            )}

            {formMsg && (
              <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: '#e6f4ea', color: '#137333', fontSize: 13 }}>
                {formMsg}
              </div>
            )}

            <button onClick={handleFormSubmit} disabled={formLoading}
              style={{ width: '100%', padding: '12px', background: formLoading ? '#dadce0' : '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: formLoading ? 'not-allowed' : 'pointer' }}>
              {formLoading ? 'Processing...' : form.action === 'add' ? 'Add to Calendar' : form.action === 'move' ? 'Move Event' : 'Cancel Event'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatAgent;

